/**
 * Employee 360 — customer-facing employee view.
 * Chapter 9 of the CX Transformation. Zero new backend — uses /api/employees/{id} which
 * already returns the employee document plus their assigned assets.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/app/StatusBadge";
import EmptyState from "@/components/app/EmptyState";
import { ArrowLeft, Rocket, RefreshCw, Undo2, Send, Mail, Phone, MapPin, Building2, Laptop, Smartphone, Monitor, Package, Loader2, AlertTriangle } from "lucide-react";
import { SendWhatsAppButton } from "@/components/whatsapp/WaComposer";
import OneFlow from "@/components/oneflow/OneFlow";

const CATEGORY_ICON = {
  Laptop: Laptop,
  Mobile: Smartphone,
  Tablet: Smartphone,
  Monitor: Monitor,
  Accessory: Package,
};

export default function EmployeeDetail() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    api.get(`/employees/${employeeId}`)
      .then(({ data }) => !cancel && setEmp(data))
      .catch((e) => !cancel && setError(e.response?.data?.detail || "We couldn't find this employee."))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [employeeId]);

  if (loading) return <div className="p-6 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  if (error) return (
    <div className="p-6">
      <Button variant="ghost" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
      <div className="mt-4 bg-white border border-slate-200 rounded-md">
        <EmptyState icon={AlertTriangle} tone="warn" title="We couldn't find this employee." description={error} testId="emp360-error" />
      </div>
    </div>
  );

  const assets = emp.assets || [];
  const byCategory = assets.reduce((m, a) => {
    const c = a.category || "Other";
    m[c] = (m[c] || 0) + 1;
    return m;
  }, {});

  const isTerminated = !emp.active || !!emp.termination_date;
  const pendingRecovery = assets.filter((a) => ["awaiting_pickup", "awaiting_inspection"].includes(a.lifecycle_state));

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="emp360">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <button className="hover:text-slate-800" onClick={() => navigate("/employees")}>Employees</button>
        <span>›</span>
        <span className="text-slate-800">{emp.name}</span>
      </div>

      {/* HERO */}
      <OneFlow flowType="EMPLOYEE" recordId={employeeId} title="Employee technology journey" />
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="flex items-start gap-5">
          <div className="h-16 w-16 rounded-full bg-slate-900 text-amber-400 grid place-items-center text-lg font-heading font-semibold shrink-0">
            {(emp.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-baseline gap-2">
              <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight" data-testid="emp360-name">{emp.name}</h1>
              <StatusBadge value={isTerminated ? "left" : "active"} tone={isTerminated ? "red" : "green"} />
            </div>
            <div className="text-sm text-slate-500 mt-0.5">{emp.employee_code} · {emp.department || "—"}</div>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
              <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-slate-400" /> {emp.email}</span>
              {emp.phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-slate-400" /> {emp.phone}</span>}
              <SendWhatsAppButton recipient={{ kind: "employee", id: emp.id, name: emp.name, phone: emp.phone, tenant_id: emp.master_company_id }} testid="send-whatsapp-employee" />
              <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-slate-400" /> {emp.country || "—"}</span>
              <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-slate-400" /> {emp.cost_centre || "—"}</span>
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0 min-w-[200px]">
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => navigate(`/deploy?employee=${emp.id}`)} data-testid="emp360-deploy">
              <Rocket className="h-4 w-4 mr-1.5" /> Deploy device
            </Button>
            <Button variant="outline" onClick={() => navigate(`/pickups?employee=${emp.id}`)} data-testid="emp360-recover">
              <Undo2 className="h-4 w-4 mr-1.5" /> Recover device
            </Button>
            <Button variant="outline" onClick={() => navigate(`/deploy?employee=${emp.id}&replace=true`)} data-testid="emp360-replace">
              <RefreshCw className="h-4 w-4 mr-1.5" /> Replace device
            </Button>
            {isTerminated && (
              <Button variant="outline" onClick={() => navigate(`/offboarding?employee=${emp.id}`)}>
                <Send className="h-4 w-4 mr-1.5" /> Offboarding checklist
              </Button>
            )}
          </div>
        </div>

        {/* Summary counts */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <SummaryTile label="Total assets"    value={assets.length} />
          <SummaryTile label="Categories"      value={Object.keys(byCategory).length} />
          <SummaryTile label="Pending recovery" value={pendingRecovery.length} tone={pendingRecovery.length ? "amber" : "slate"} />
          <SummaryTile label="Replacement due" value={assets.filter((a) => (a.condition || "A") >= "C").length} tone="slate" />
        </div>
      </div>

      {/* CURRENT TECHNOLOGY */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <h2 className="text-lg font-heading font-semibold text-slate-900 tracking-tight">Current technology</h2>
            <div className="text-xs text-slate-500">{assets.length} device{assets.length === 1 ? "" : "s"} currently held by {emp.name.split(" ")[0]}.</div>
          </div>
        </div>
        {assets.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md">
            <EmptyState
              icon={Laptop}
              tone="neutral"
              title="No devices assigned yet"
              description="This employee doesn't hold any devices right now. Use Deploy Device above to assign their first one."
              testId="emp360-no-assets"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {assets.map((a) => {
              const Icon = CATEGORY_ICON[a.category] || Package;
              return (
                <button
                  key={a.id}
                  onClick={() => navigate(`/assets?open=${a.id}`)}
                  data-testid={`emp360-asset-${a.id}`}
                  className="text-left bg-white border border-slate-200 rounded-md p-4 hover:border-slate-300 hover:-translate-y-px hover:shadow-sm transition-[transform,box-shadow,border-color] flex gap-3"
                >
                  <div className="h-10 w-10 rounded-md bg-slate-50 border border-slate-100 grid place-items-center shrink-0">
                    <Icon className="h-5 w-5 text-slate-500" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{a.brand} {a.model}</div>
                    <div className="text-xs text-slate-500 mt-0.5 font-mono truncate">Serial ***{(a.serial || "").slice(-4)}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <StatusBadge value={a.lifecycle_state} />
                      <StatusBadge value={a.condition} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Manager & work info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-500">Work details</div>
          <dl className="mt-2 text-sm space-y-1">
            <Row k="Department" v={emp.department} />
            <Row k="Cost centre" v={emp.cost_centre} />
            <Row k="Manager" v={emp.manager_email} />
            <Row k="Joined" v={emp.joining_date} />
            {emp.termination_date && <Row k="Termination" v={emp.termination_date} />}
          </dl>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-500">Delivery</div>
          <div className="mt-2 text-sm text-slate-800 leading-relaxed">
            {emp.delivery_address || "No delivery address on file — please update before the next deployment."}
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, tone = "slate" }) {
  const c = { slate: "text-slate-900", amber: "text-amber-700", rose: "text-rose-700", emerald: "text-emerald-700" }[tone];
  return (
    <div className="bg-slate-50 border border-slate-100 rounded-md p-3">
      <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-heading font-bold tabular-nums ${c}`}>{value}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500">{k}</dt>
      <dd className="text-slate-800 text-right">{v || "—"}</dd>
    </div>
  );
}
